import React, { useState, useEffect, useCallback, FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getCurrentBranchTime } from '../../utils/timezoneUtils';
import moment from 'moment-timezone';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Autocomplete,
  Snackbar,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BarcodeIcon from '@mui/icons-material/QrCodeScanner';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import { v4 as uuidv4 } from 'uuid';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import arSA from 'date-fns/locale/ar-SA';

// Importar tipos y componentes
import { WarehouseTransferRequestFormData } from '../../types/warehouseTransferRequest';
import { ProductVariant } from '../../types/product';
import BarcodeScanner from '../common/BarcodeScanner';
import { API_BASE_URL } from '../../config';
import ProductAttributeSearch from '../invoices/ProductAttributeSearch';

// Definir tipos
interface Warehouse {
  _id: string;
  name: string;
}

interface WarehouseState {
  fromWarehouses: Warehouse[];  // مستودعات الفرع فقط لخانة "من المستودع"
  toWarehouses: Warehouse[];    // جميع المستودعات لخانة "إلى المستودع"
}

interface Unit {
  _id: string;
  name: string;
}

interface InventoryItem {
  product: string;
  productName: string;
  variantId: string | null;
  variantAttributes: Record<string, string>;
  quantity: number;
  unit: string;
  unitName: string;
  sku: string;
  barcode: string;
}

interface TransferItem {
  product: string;
  productName: string;
  variantId: string | null;
  variantAttributes: Record<string, string>;
  quantity: number;
  unit: string;
  unitName: string;
  sku: string;
  barcode: string;
  tempId: string;
  hasError?: boolean;
}

interface GroupedProduct {
  productId: string;
  productName: string;
  variants: InventoryItem[];
}

interface Product {
  _id?: string;
  name?: string;
  description?: string;
  variants?: ProductVariant[];
}

// The component's function signature needs to explicitly return React.ReactElement
function WarehouseTransferRequestForm(): React.ReactElement {
  const navigate = useNavigate();
  const { id: requestId } = useParams<{ id?: string }>();
  const isEditMode = !!requestId;
  const { user, activeBranch } = useAuth();
  const { hasPermission } = usePermissions();

  const [warehouses, setWarehouses] = useState<WarehouseState>({
    fromWarehouses: [],
    toWarehouses: []
  });
  const [units, setUnits] = useState<Unit[]>([]);
  const [defaultUnitId, setDefaultUnitId] = useState<string>('');
  const [fromWarehouse, setFromWarehouse] = useState<Warehouse | null>(null);
  const [toWarehouse, setToWarehouse] = useState<Warehouse | null>(null);
  const [transferDate, setTransferDate] = useState<string>(() => {
    const branchTime = getCurrentBranchTime();
    return branchTime.format('YYYY-MM-DD'); // Format: YYYY-MM-DD
  });
  const [notes, setNotes] = useState<string>('');
  const [items, setItems] = useState<TransferItem[]>([]);
  
  // Product search
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<InventoryItem[]>([]);
  const [groupedResults, setGroupedResults] = useState<GroupedProduct[]>([]);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [filterTerm, setFilterTerm] = useState<string>('');
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  
  // Product Attribute Search state
  const [isProductAttributeSearchOpen, setIsProductAttributeSearchOpen] = useState<boolean>(false);
  
  // Barcode scanning
  const [isScannerOpen, setIsScannerOpen] = useState<boolean>(false);
  const [barcode, setBarcode] = useState<string>('');
  
  // Excel import/export
  const [isExcelImportLoading, setIsExcelImportLoading] = useState<boolean>(false);
  
  // Alerts and loading states
  const [alert, setAlert] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  
  // Group search results by product
  const groupSearchResults = useCallback((results: InventoryItem[]) => {
    const groupedMap = new Map<string, GroupedProduct>();
    
    results.forEach(item => {
      if (!groupedMap.has(item.product)) {
        groupedMap.set(item.product, {
          productId: item.product,
          productName: item.productName,
          variants: []
        });
      }
      
      const group = groupedMap.get(item.product)!;
      group.variants.push(item);
    });
    
    return Array.from(groupedMap.values());
  }, []);
  
  // Set grouped results whenever search results change
  useEffect(() => {
    const grouped = groupSearchResults(searchResults);
    setGroupedResults(grouped);
  }, [searchResults, groupSearchResults]);
  
  // Filtered search results
  const filteredGroupedResults = React.useMemo(() => {
    if (!filterTerm) return groupedResults;
    
    const lowercasedFilter = filterTerm.toLowerCase();
    
    return groupedResults
      .map(group => {
        const filteredVariants = group.variants.filter(item =>
          item.productName.toLowerCase().includes(lowercasedFilter) ||
          (item.sku && item.sku.toLowerCase().includes(lowercasedFilter)) ||
          (item.barcode && item.barcode.toLowerCase().includes(lowercasedFilter)) ||
          Object.entries(item.variantAttributes || {}).some(
            ([key, value]) => String(value).toLowerCase().includes(lowercasedFilter)
          )
        );
        
        if (filteredVariants.length === 0) return null;
        
        return {
          ...group,
          variants: filteredVariants
        };
      })
      .filter((group): group is GroupedProduct => group !== null);
  }, [groupedResults, filterTerm]);

  // Load warehouses and units on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      
      try {
        // Fetch warehouses for "fromWarehouse" (branch warehouses only)
        let fromWarehousesResponse;
        try {
          const { apiWithContext } = await import('../../services/api');
          fromWarehousesResponse = await apiWithContext.get('/warehouse-transfer-requests/warehouses', 'warehouse-transfer-request');
        } catch (error) {
          fromWarehousesResponse = await axios.get(`${API_BASE_URL}/warehouses`);
        }
        
        // Fetch all warehouses for "toWarehouse"
        let toWarehousesResponse;
        try {
          const { apiWithContext } = await import('../../services/api');
          toWarehousesResponse = await apiWithContext.get('/warehouse-transfer-requests/warehouses/all', 'warehouse-transfer-request');
        } catch (error) {
          toWarehousesResponse = await axios.get(`${API_BASE_URL}/warehouses`);
        }
        
        if (fromWarehousesResponse.data.success && toWarehousesResponse.data.success) {
          setWarehouses({
            fromWarehouses: fromWarehousesResponse.data.warehouses,
            toWarehouses: toWarehousesResponse.data.warehouses
          });
        }
        
        // Fetch available units
        const { apiWithContext } = await import('../../services/api');
        const unitsResponse = await apiWithContext.get('/units', 'warehouse-transfer-request');
        if (unitsResponse.data.success) {
          setUnits(unitsResponse.data.units);
          
          // Set default unit ID (piece or first available unit)
          const pieceUnit = unitsResponse.data.units.find((u: Unit) => u.name === 'قطعة' || u.name === 'piece');
          if (pieceUnit) {
            setDefaultUnitId(pieceUnit._id);
          } else if (unitsResponse.data.units.length > 0) {
            setDefaultUnitId(unitsResponse.data.units[0]._id);
          }
        }
      } catch (error: any) {
        setAlert({
          type: 'error',
          message: `خطأ في تحميل البيانات الأساسية: ${error.response?.data?.message || error.message}`
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Load existing transfer request data in edit mode (after warehouses are loaded)
  useEffect(() => {
    const loadRequestData = async () => {
      if (isEditMode && requestId && warehouses.fromWarehouses.length > 0 && warehouses.toWarehouses.length > 0) {
        try {
          const requestResponse = await axios.get(`${API_BASE_URL}/warehouse-transfer-requests/${requestId}`);
          
          if (requestResponse.data.success) {
            const request = requestResponse.data.request;
            
            // Find warehouses in loaded data
            const fromWarehouseId = request.fromWarehouse?._id || request.fromWarehouse;
            const toWarehouseId = request.toWarehouse?._id || request.toWarehouse;
            
            const fromWarehouseObj = warehouses.fromWarehouses.find(w => w._id === fromWarehouseId) || null;
            const toWarehouseObj = warehouses.toWarehouses.find(w => w._id === toWarehouseId) || null;
            
            // Set form data
            setFromWarehouse(fromWarehouseObj);
            setToWarehouse(toWarehouseObj);
            setNotes(request.notes || '');
            
            // Set transfer date
            if (request.date) {
              const date = new Date(request.date);
              setTransferDate(date.toISOString().split('T')[0]);
            }
            
          // Set product variants
          if (request.productVariants && request.productVariants.length > 0) {
            const variants = request.productVariants.map(item => ({
              tempId: Date.now() + Math.random().toString(),
              // التحقق من أن product هو string ID أو object وجلب _id منه
              product: item.product?._id || item.product,
              productName: item.productName,
              // التحقق من أن variantId هو string ID أو object وجلب _id منه
              variantId: item.variantId?._id || item.variantId,
              variantAttributes: item.variantAttributes || {},
              quantity: item.quantity,
              // التحقق من أن unit هو string ID أو object وجلب _id منه
              unit: item.unit?._id || item.unit,
              unitName: item.unitName,
              sku: item.sku || '',
              barcode: item.barcode || ''
            }));
            setItems(variants);
          }
          }
        } catch (error: any) {
          setAlert({
            type: 'error',
            message: `خطأ في تحميل بيانات الطلب: ${error.response?.data?.message || error.message}`
          });
        }
      }
    };

    loadRequestData();
  }, [warehouses.fromWarehouses, warehouses.toWarehouses, isEditMode, requestId]);

  // Handle barcode scan
  const handleBarcodeScan = useCallback(async (barcode: string) => {
    if (!fromWarehouse) {
      setAlert({
        type: 'error',
        message: 'الرجاء اختيار المستودع المصدر أولاً'
      });
      return;
    }

    try {
      setIsLoading(true);
      // البحث عن المنتج بالباركود في قائمة المنتجات (وليس في المخزون)
      // إرسال الباركود في body بدلاً من URL لتجنب مشاكل الترميز مع الأحرف الخاصة مثل /
      const { apiWithContext } = await import('../../services/api');
      const response = await apiWithContext.post(`/products/search-by-barcode`, { barcode }, 'warehouse-transfer-request');
      
      if (response.data.success && response.data.product) {
        const product = response.data.product;
        const matchingVariant = response.data.variant;
        
        // تحديد معلومات المتغير (إذا وجد)
        let variantId = null;
        let variantAttributes: Record<string, string> = {};
        let sku = product.sku || '';
        
        if (matchingVariant) {
          variantId = matchingVariant._id;
          variantAttributes = matchingVariant.attributes || {};
          sku = matchingVariant.sku || sku;
        }
        
        // Check if this item already exists in the list
        const existingItemIndex = items.findIndex(
          item => item.product === product._id &&
                 (variantId ? item.variantId === variantId : !item.variantId)
        );
        
        if (existingItemIndex >= 0) {
          // Update existing item quantity
          const updatedItems = [...items];
          updatedItems[existingItemIndex].quantity += 1;
          setItems(updatedItems);
        } else {
          // Add new item with validated unit ID
          const { unitId, unitName } = getValidUnitId(defaultUnitId);
          
          const newItem: TransferItem = {
            product: product._id,
            productName: product.name,
            variantId: variantId,
            variantAttributes: variantAttributes,
            quantity: 1,
            unit: unitId,
            unitName: unitName,
            sku: sku,
            barcode: barcode,
            tempId: Math.random().toString(36).substring(2, 9)
          };
          setItems(prevItems => [...prevItems, newItem]);
        }
        
        setAlert({
          type: 'success',
          message: `تمت إضافة ${product.name} بنجاح`
        });
      } else {
        setAlert({
          type: 'error',
          message: `لم يتم العثور على منتج بالباركود ${barcode}`
        });
      }
    } catch (error) {
      console.error('Error finding product by barcode:', error);
      setAlert({
        type: 'error',
        message: 'حدث خطأ أثناء البحث عن الباركود'
      });
    } finally {
      setIsLoading(false);
      setIsScannerOpen(false);
    }
  }, [fromWarehouse, items]);

  // Toggle product expansion in search results
  const toggleProductExpansion = (productId: string) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  // Handle product search in warehouse inventory
  const handleSearch = async () => {
    if (!fromWarehouse || !searchTerm) {
      setAlert({
        type: 'error',
        message: 'الرجاء اختيار المستودع المصدر وإدخال مصطلح البحث'
      });
      return;
    }
  
    try {
      setIsSearching(true);
      // Using detailed search to get all product variants
      const response = await axios.get(`${API_BASE_URL}/inventory/search?term=${searchTerm}&warehouse=${fromWarehouse._id}&includeDetails=true&includeVariants=true`);
      
      if (response.data.success && response.data.items.length > 0) {
        setSearchResults(response.data.items);
        // Auto-expand products with multiple variants
        const grouped = groupSearchResults(response.data.items);
        const autoExpand = new Set<string>();
        grouped.forEach(group => {
          if (group.variants.length > 1) {
            autoExpand.add(group.productId);
          }
        });
        setExpandedProducts(autoExpand);
        setIsSearchDialogOpen(true);
      } else {
        setAlert({
          type: 'error',
          message: 'لم يتم العثور على نتائج'
        });
      }
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'حدث خطأ أثناء البحث'
      });
    } finally {
      setIsSearching(false);
    }
  };

  // State for item quantity in dialog
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);

  // Handle product selection from ProductAttributeSearch
  const handleProductAttributeSelect = (product: Product, variant: ProductVariant) => {
    const defaultBarcode = variant.barcodes.find(b => b.isDefault) || variant.barcodes[0];
    
    // Validate MongoDB ObjectId pattern for units
    const isValidObjectId = (id: string) => /^[0-9a-fA-F]{24}$/.test(id);
    
    // Get unit information
    let unitId = defaultBarcode?.unit || variant.unit || (units.length > 0 ? units[0]._id : '');
    let unitName = defaultBarcode?.unit || variant.unit || (units.length > 0 ? units[0].name : 'قطعة');
    
    // Find the unit in our units array to get its name
    if (unitId && isValidObjectId(unitId)) {
      const unit = units.find(u => u._id === unitId);
      if (unit) {
        unitName = unit.name;
      } else {
        // The unit ID exists but doesn't match any unit in the array
        // Try to find a valid unit instead
        unitId = ''; // Reset to trigger fallback logic
      }
    }
    
    // Fallback if unitId is not set or invalid
    if (!unitId || !isValidObjectId(unitId)) {
      if (units.length > 0) {
        // Find the first valid unit in the array
        const validUnit = units.find(u => isValidObjectId(u._id));
        if (validUnit) {
          unitId = validUnit._id;
          unitName = validUnit.name;
        } else {
          // Error: No valid unit found
          setAlert({
            type: 'error',
            message: 'لا توجد وحدة صالحة متاحة. الرجاء التحقق من إعدادات الوحدات.'
          });
          return; // Don't proceed with adding the item
        }
      } else {
        // Error: No units available
        setAlert({
          type: 'error',
          message: 'لم يتم العثور على وحدات. الرجاء إضافة وحدات قبل إضافة منتجات.'
        });
        return; // Don't proceed with adding the item
      }
    }

    const newItem: TransferItem = {
      product: product._id || '',
      productName: product.name || '',
      variantId: variant._id || null,
      variantAttributes: variant.attributes || {},
      quantity: 1,
      unit: unitId,
      unitName: unitName,
      sku: variant.sku || '',
      barcode: defaultBarcode?.code || '',
      tempId: Date.now().toString() + Math.random().toString()
    };

    const existingItemIndex = items.findIndex(
      item => item.product === newItem.product && item.variantId === newItem.variantId
    );

    if (existingItemIndex >= 0) {
      const updatedItems = [...items];
      updatedItems[existingItemIndex].quantity += 1;
      setItems(updatedItems);
    } else {
      setItems([...items, newItem]);
    }

    setIsProductAttributeSearchOpen(false);
    setAlert({
      type: 'success',
      message: 'تمت إضافة المنتج بنجاح'
    });
  };

  // Handle multiple product selection from ProductAttributeSearch
  const handleMultiProductAttributeSelect = (selectedItems: { product: Product; variant: ProductVariant }[]) => {
    const newItems: TransferItem[] = [...items];

    selectedItems.forEach(({ product, variant }) => {
      const defaultBarcode = variant.barcodes.find(b => b.isDefault) || variant.barcodes[0];
      
      // Validate MongoDB ObjectId pattern for units
      const isValidObjectId = (id: string) => /^[0-9a-fA-F]{24}$/.test(id);
      
      // Get unit information
      let unitId = defaultBarcode?.unit || variant.unit || (units.length > 0 ? units[0]._id : '');
      let unitName = defaultBarcode?.unit || variant.unit || (units.length > 0 ? units[0].name : 'قطعة');
      
      // Find the unit in our units array to get its name
      if (unitId && isValidObjectId(unitId)) {
        const unit = units.find(u => u._id === unitId);
        if (unit) {
          unitName = unit.name;
        } else {
          // The unit ID exists but doesn't match any unit in the array
          // Try to find a valid unit instead
          unitId = ''; // Reset to trigger fallback logic
        }
      }
      
      // Fallback if unitId is not set or invalid
      if (!unitId || !isValidObjectId(unitId)) {
        if (units.length > 0) {
          // Find the first valid unit in the array
          const validUnit = units.find(u => isValidObjectId(u._id));
          if (validUnit) {
            unitId = validUnit._id;
            unitName = validUnit.name;
          } else {
            // Error: No valid unit found - skip this item
            console.error('لا توجد وحدة صالحة متاحة للمنتج:', product.name);
            return; // Skip this item
          }
        } else {
          // Error: No units available - skip this item
          console.error('لم يتم العثور على وحدات للمنتج:', product.name);
          return; // Skip this item
        }
      }

      const newItem: TransferItem = {
        product: product._id || '',
        productName: product.name || '',
        variantId: variant._id || null,
        variantAttributes: variant.attributes || {},
        quantity: 1,
        unit: unitId,
        unitName: unitName,
        sku: variant.sku || '',
        barcode: defaultBarcode?.code || '',
        tempId: Date.now().toString() + Math.random().toString()
      };

      const existingItemIndex = newItems.findIndex(
        item => item.product === newItem.product && item.variantId === newItem.variantId
      );

      if (existingItemIndex >= 0) {
        newItems[existingItemIndex].quantity += 1;
      } else {
        newItems.push(newItem);
      }
    });

    setItems(newItems);
    setIsProductAttributeSearchOpen(false);
    setAlert({
      type: 'success',
      message: `تمت إضافة ${selectedItems.length} منتج بنجاح`
    });
  };

  // Add product from search results with specific quantity
  const addProductFromSearch = (inventoryItem: InventoryItem, quantity: number = 1) => {
    // Check if item already exists in the list
    const existingItemIndex = items.findIndex(
      item => item.product === inventoryItem.product &&
             item.variantId === inventoryItem.variantId
    );
    
    if (existingItemIndex >= 0) {
      // Update existing item quantity
      const updatedItems = [...items];
      updatedItems[existingItemIndex].quantity += quantity;
      setItems(updatedItems);
    } else {
      // Add new item with all variant details
      const newItem: TransferItem = {
        product: inventoryItem.product,
        productName: inventoryItem.productName,
        variantId: inventoryItem.variantId,
        variantAttributes: inventoryItem.variantAttributes || {},
        quantity: quantity,
        unit: inventoryItem.unit,
        unitName: inventoryItem.unitName,
        sku: inventoryItem.sku,
        barcode: inventoryItem.barcode,
        tempId: Math.random().toString(36).substring(2, 9)
      };
      
      // تم إزالة التحقق من الكمية المتاحة هنا للسماح بالكميات السالبة
      
      setItems(prevItems => [...prevItems, newItem]);
    }
    
    setIsSearchDialogOpen(false);
    setSearchTerm('');
    setFilterTerm('');
    setSelectedQuantity(1);
    setSelectedItem(null);
    setSearchResults([]);
    setAlert({
      type: 'success',
      message: 'تمت إضافة المنتج بنجاح'
    });
  };

  // Show quantity dialog for a product
  const showQuantityDialog = (item: InventoryItem) => {
    setSelectedItem(item);
    setSelectedQuantity(1);
  };

  // Reset product selection
  const cancelProductSelection = () => {
    setSelectedItem(null);
    setSelectedQuantity(1);
  };

  // Update item quantity
  const updateItemQuantity = (index: number, quantity: number) => {
    if (quantity <= 0) return;
    
    const updatedItems = [...items];
    updatedItems[index].quantity = quantity;
    setItems(updatedItems);
  };

  // Remove item
  const removeItem = (index: number) => {
    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    setItems(updatedItems);
  };

  // Save transfer request
  const saveTransferRequest = async () => {
    if (!fromWarehouse || !toWarehouse) {
      setAlert({
        type: 'error',
        message: 'الرجاء اختيار المستودع المصدر والمستودع الوجهة'
      });
      return;
    }

    if (fromWarehouse._id === toWarehouse._id) {
      setAlert({
        type: 'error',
        message: 'لا يمكن التحويل إلى نفس المستودع'
      });
      return;
    }

    if (items.length === 0) {
      setAlert({
        type: 'error',
        message: 'الرجاء إضافة منتجات للتحويل'
      });
      return;
    }

    // Define type for item validation issues
    interface InvalidItem {
      index: number;
      name: string;
      issues: string[];
    }
    
    // Improved validation for items
    const invalidItems: InvalidItem[] = [];
    const itemsWithIssues: number[] = [];
    
    // Validate MongoDB ObjectId pattern
    const isValidObjectId = (id: string) => /^[0-9a-fA-F]{24}$/.test(id);
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const issues: string[] = [];
      
      // Check for valid product ID
      if (!item.product || !isValidObjectId(item.product)) {
        issues.push('معرف المنتج غير صالح');
      }
      
      // Check for valid unit ID
      if (!item.unit || !isValidObjectId(item.unit)) {
        issues.push('معرف الوحدة غير صالح');
      }
      
      // Check for positive quantity
      if (!item.quantity || item.quantity <= 0) {
        issues.push('الكمية يجب أن تكون أكبر من صفر');
      }
      
      if (issues.length > 0) {
        invalidItems.push({
          index: i + 1,
          name: item.productName,
          issues: issues
        });
        itemsWithIssues.push(i);
      }
    }
    
    if (invalidItems.length > 0) {
      // Create detailed error message
      const errorDetails = invalidItems.map(item =>
        `المنتج ${item.index}: ${item.name} - ${item.issues.join(', ')}`
      ).join('\n');
      
      console.error('Invalid items detected:', errorDetails);
      
      // Mark problem items in the UI
      const updatedItems = [...items];
      itemsWithIssues.forEach(index => {
        updatedItems[index] = {
          ...updatedItems[index],
          hasError: true
        };
      });
      setItems(updatedItems);
      
      // Show detailed error message to the user
      setAlert({
        type: 'error',
        message: `بعض المنتجات تحتوي على بيانات غير صالحة:\n${errorDetails}`
      });
      return;
    }
try {
  setIsSaving(true);
  
  // Get token from localStorage
  const authToken = localStorage.getItem('token');
  
  // Check for token existence before proceeding
  if (!authToken) {
    setAlert({
      type: 'error',
      message: 'لم يتم العثور على بيانات تسجيل الدخول. يرجى تسجيل الدخول مرة أخرى للمتابعة.'
    });
    
    // Delay before redirecting to give user time to read the message
    setTimeout(() => {
      window.location.href = '/login';
    }, 3000);
    
    setIsSaving(false);
    return;
  }
  
  // Log token presence for debugging
  console.log('Authentication token available:', true);
  
  // Get user info from the useAuth hook called at the top level
  let userId = user?._id || '';
  
  // If no user in context, try to get from localStorage
  if (!userId) {
    try {
      const userDataStr = localStorage.getItem('userData');
          if (userDataStr) {
            const userData = JSON.parse(userDataStr);
            userId = userData._id || '';
            console.log('User ID from localStorage:', userId);
          }
        } catch (e) {
          console.error('Error getting user data:', e);
        }
      }
      
      // If still no user ID, use the mock user ID from the API config
      if (!userId) {
        userId = "64067927a9259bbde06c6f15"; // Mock user ID from api.ts
        console.log('Using mock user ID:', userId);
      }
      
      // Check if request data is valid
      if (!fromWarehouse || !fromWarehouse._id) {
        throw new Error('المستودع المصدر غير محدد بشكل صحيح');
      }
      
      if (!toWarehouse || !toWarehouse._id) {
        throw new Error('المستودع الهدف غير محدد بشكل صحيح');
      }
      
      if (items.length === 0) {
        throw new Error('يجب إضافة منتج واحد على الأقل');
      }
      
      // Validate each item
      for (const item of items) {
        if (!item.product) {
          throw new Error('أحد المنتجات غير محدد بشكل صحيح');
        }
        if (!item.quantity || item.quantity <= 0) {
          throw new Error(`الكمية غير صالحة للمنتج ${item.productName}`);
        }
        if (!item.unit) {
          throw new Error(`الوحدة غير محددة للمنتج ${item.productName}`);
        }
      }
      
      // Prepare request data with createdBy field - ensure all fields are properly formatted
      // Get branch timezone
      const branchTimezone = activeBranch?.timezone || 'Asia/Riyadh';
      
      // Convert transferDate to branch timezone with time
      let dateToSend: string;
      if (transferDate) {
        const [year, month, day] = transferDate.split('-').map(Number);
        const now = moment.tz(branchTimezone);
        const dateWithTime = moment.tz({
          year,
          month: month - 1,
          day,
          hour: now.hour(),
          minute: now.minute(),
          second: now.second()
        }, branchTimezone);
        dateToSend = dateWithTime.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
      } else {
        dateToSend = moment.tz(branchTimezone).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
      }
      
      const requestData = {
        fromWarehouse: fromWarehouse._id.toString(),
        toWarehouse: toWarehouse._id.toString(),
        date: dateToSend,
        notes: notes || '',
        createdBy: userId, // Add the required createdBy field
        productVariants: items.map(item => ({
          product: item.product.toString(),
          productName: item.productName || '',
          variantId: item.variantId || null,
          variantAttributes: item.variantAttributes || {},
          quantity: Number(item.quantity),
          unit: item.unit.toString(),
          unitName: item.unitName || '',
          sku: item.sku || '',
          barcode: item.barcode || ''
        }))
      };
      
      // Set up authentication headers - try both token formats
      const headers = {
        'Content-Type': 'application/json',
        'X-User-ID': userId || '' // Add X-User-ID header as well for compatibility
      };
      
      // Only add auth headers if token exists
      if (authToken) {
        headers['x-auth-token'] = authToken;
        headers['Authorization'] = `Bearer ${authToken}`;
        console.log('Authentication headers added');
      } else {
        console.warn('No authentication token available - API request may fail with 401 error');
      }
      
      console.log('Saving warehouse transfer request with authentication and user ID...');
      console.log('Request data:', JSON.stringify(requestData, null, 2));
      
      // Use a tracking variable for successful response
      let responseData = null;
      let success = false;
      
      // First try using the api instance which has proper interceptors
      try {
        console.log('Trying with api instance first...');
        
        let response;
        if (isEditMode && requestId) {
          response = await api.put(
            `/warehouse-transfer-requests/${requestId}`,
            requestData
          );
        } else {
          response = await api.post(
            `/warehouse-transfer-requests`,
            requestData
          );
        }
        
        console.log('API response:', response);
        responseData = response.data;
        success = true;
      } catch (err: any) {
        console.error('API instance attempt failed:', err);
        console.error('Error response:', err.response);
        
        // Continue to try direct axios calls
      }
      
      // If api instance failed, try direct axios calls with various endpoints
      if (!success) {
        // Array of endpoints to try in order - don't include /api prefix as it's already in API_BASE_URL
        const endpoints = [
          '/warehouse-transfer-requests',
          '/warehouse-transfers/requests',
          '/warehouseTransferRequests'
        ];
        
        // Try each endpoint until one works
        for (const endpoint of endpoints) {
          if (success) break;
          
          try {
            console.log(`Trying direct endpoint: ${endpoint}`);
            
            let response;
            if (isEditMode && requestId) {
              response = await axios.put(
                `${API_BASE_URL}${endpoint}/${requestId}`,
                requestData,
                { headers }
              );
            } else {
              response = await axios.post(
                `${API_BASE_URL}${endpoint}`,
                requestData,
                { headers }
              );
            }
            
            console.log('Response received:', response.status, response.data);
            
            // If we get here, the request was successful
            responseData = response.data;
            success = true;
            break;
          } catch (err: any) {
            console.error(`Failed with endpoint ${endpoint}:`, err.message);
            console.error('Status:', err.response?.status);
            console.error('Error data:', err.response?.data);
            
            // Handle authentication errors specifically
            if (err.response?.status === 401) {
              // Clear the invalid token
              localStorage.removeItem('token');
              
              setAlert({
                type: 'error',
                message: 'خطأ في المصادقة: جلسة العمل انتهت. سيتم توجيهك إلى صفحة تسجيل الدخول.'
              });
              
              setIsSaving(false);
              
              // Delay before redirecting to give user time to read the message
              setTimeout(() => {
                window.location.href = '/login';
              }, 3000);
              
              // Break the loop - no need to try other endpoints if authentication failed
              return; // Exit the function completely
            }
            // Continue to the next endpoint for other types of errors
          }
        }
      }
      
      // Handle success
      if (success) {
        setAlert({
          type: 'success',
          message: isEditMode ?
            'تم تحديث طلب التحويل بنجاح' :
            'تم إنشاء طلب التحويل بنجاح وهو الآن تحت التنفيذ'
        });
        
        // Navigate to request list after a delay
        setTimeout(() => {
          navigate('/warehouse-transfers/requests');
        }, 2000);
      } else {
        throw new Error('فشل حفظ طلب التحويل بعد محاولة جميع الطرق المتاحة');
      }
    } catch (error: any) {
      console.error('Error saving warehouse transfer request:', error);
      setAlert({
        type: 'error',
        message: error.response?.data?.message || error.message || 'حدث خطأ أثناء حفظ طلب التحويل، يرجى التحقق من الاتصال بالخادم'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // وظيفة لتنزيل قالب الإكسل المبسط للمنتجات
  const handleDownloadExcelTemplate = () => {
    try {
      // إنشاء بيانات القالب - حقول مبسطة
      const templateData = [
        {
          'الباركود': '',
          'الكمية': '',
          'وحدة القياس': ''
        }
      ];

      // إنشاء ورقة عمل جديدة
      const worksheet = XLSX.utils.json_to_sheet(templateData);
      
      // تعيين عرض الأعمدة
      const columnWidths = [
        { wch: 20 }, // الباركود
        { wch: 10 }, // الكمية
        { wch: 15 }, // وحدة القياس
      ];
      
      worksheet['!cols'] = columnWidths;
      
      // إضافة تعليمات وعناوين الأعمدة
      XLSX.utils.sheet_add_aoa(worksheet, [
        ['الباركود', 'الكمية', 'وحدة القياس'],
        ['', '', ''],
        ['تعليمات: أدخل باركود المنتج، الكمية المطلوبة، ووحدة القياس (مثل: قطعة، كرتون، كيلو)']
      ], { origin: { r: 0, c: 0 } });
      
      // إنشاء مصنف جديد
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'منتجات التحويل');

      // تنزيل الملف
      XLSX.writeFile(workbook, 'قالب_منتجات_التحويل.xlsx');
      
      // عرض رسالة نجاح
      setAlert({
        type: 'success',
        message: 'تم تنزيل قالب الإكسل بنجاح'
      });
    } catch (error) {
      console.error('Error generating Excel template:', error);
      setAlert({
        type: 'error',
        message: 'حدث خطأ أثناء إنشاء قالب الإكسل'
      });
    }
  };

  // وظيفة لاستيراد المنتجات من ملف إكسل - تركز على الباركود والكمية ووحدة القياس
  const handleExcelFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!fromWarehouse) {
      setAlert({
        type: 'error',
        message: 'الرجاء اختيار المستودع المصدر أولاً'
      });
      return;
    }
    
    const fileInput = event.target;
    if (!fileInput.files || fileInput.files.length === 0) {
      return;
    }
    
    const file = fileInput.files[0];
    setIsExcelImportLoading(true);
    
    try {
      // قراءة الملف
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      
      // الحصول على الورقة الأولى
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      // تحويل البيانات إلى JSON
      const products = XLSX.utils.sheet_to_json<{
        'الباركود': string,
        'الكمية'?: number | string,
        'وحدة القياس'?: string
      }>(worksheet);
      
      if (products.length === 0) {
        setAlert({
          type: 'error',
          message: 'ملف الإكسل لا يحتوي على بيانات'
        });
        setIsExcelImportLoading(false);
        return;
      }
      
      // معالجة كل منتج - جمع الصفوف الصالحة أولاً
      let successCount = 0;
      let errorCount = 0;
      let skippedCount = 0;

      interface ParsedRequestRow { rowNumber: number; barcode: string; quantity: number; unitName: string; }
      const parsedRows: ParsedRequestRow[] = [];
      const seenBarcodes = new Map<string, ParsedRequestRow>();

      for (let i = 0; i < products.length; i++) {
        const product = products[i];

        // تجاهل الصفوف الفارغة
        if (!product['الباركود']) {
          skippedCount++;
          continue;
        }

        const barcode = product['الباركود'].toString().trim();
        if (!barcode) {
          skippedCount++;
          continue;
        }

        // تحديد الكمية، الافتراضي هو 1
        const quantity = typeof product['الكمية'] === 'number' ? product['الكمية'] :
                        (typeof product['الكمية'] === 'string' && product['الكمية'] ? parseFloat(product['الكمية']) : 1);

        const unitName = product['وحدة القياس'] || '';

        // توحيد السلوك مع التحويل المباشر: تجميع الكميات عند تكرار نفس الباركود داخل الملف
        const key = barcode.toLowerCase();
        const existingRow = seenBarcodes.get(key);
        if (existingRow) {
          existingRow.quantity += quantity;
          continue;
        }

        const parsedRow: ParsedRequestRow = { rowNumber: i + 2, barcode, quantity, unitName };
        seenBarcodes.set(key, parsedRow);
        parsedRows.push(parsedRow);
      }

      // بحث مجمّع واحد لكل الباركودات بدل طلب لكل صف (مقسّم إلى دفعات عند الحاجة)
      const CHUNK_SIZE = 500;
      const barcodeList = parsedRows.map(r => r.barcode);
      const barcodeResults = new Map<string, any>();

      if (barcodeList.length > 0) {
        const { apiWithContext } = await import('../../services/api');

        for (let start = 0; start < barcodeList.length; start += CHUNK_SIZE) {
          const chunk = barcodeList.slice(start, start + CHUNK_SIZE);
          const searchResponse = await apiWithContext.post(
            `/inventory/search-barcodes`,
            { barcodes: chunk, warehouse: fromWarehouse._id },
            'warehouse-transfer-request',
            { timeout: 120000 }
          );

          const chunkResults: any[] = searchResponse.data?.results || [];
          chunkResults.forEach((r: any) => {
            if (r && r.barcode) {
              barcodeResults.set(r.barcode.toString().trim().toLowerCase(), r);
            }
          });
        }
      }

      const newItems: TransferItem[] = [...items];

      for (const parsedRow of parsedRows) {
        const match = barcodeResults.get(parsedRow.barcode.trim().toLowerCase());

        if (!match || !match.found) {
          errorCount++;
          continue;
        }

        // استخدام وحدة القياس المحددة في الإكسل إذا كانت موجودة وإلا الافتراضية
        const unitData = parsedRow.unitName ?
          findUnitByName(parsedRow.unitName) :
          getValidUnitId(defaultUnitId);

        const variantId = match.variantId || null;

        // التحقق مما إذا كان المنتج موجودًا بالفعل في القائمة
        const existingItemIndex = newItems.findIndex(
          item => item.product === match.productId &&
                 (variantId ? item.variantId === variantId : !item.variantId)
        );

        if (existingItemIndex >= 0) {
          newItems[existingItemIndex].quantity += parsedRow.quantity;
        } else {
          newItems.push({
            product: match.productId,
            productName: match.productName || '',
            variantId: variantId,
            variantAttributes: match.variantAttributes || {},
            quantity: parsedRow.quantity,
            unit: unitData.unitId,
            unitName: unitData.unitName,
            sku: match.sku || '',
            barcode: match.mainBarcode || parsedRow.barcode,
            tempId: Math.random().toString(36).substring(2, 9)
          });
        }

        successCount++;
      }
      
      // تحديث قائمة المنتجات
      setItems(newItems);
      
      // عرض ملخص النتائج
      setAlert({
        type: successCount > 0 ? 'success' : 'error',
        message: `تم استيراد المنتجات: ${successCount} بنجاح، ${errorCount} فشل، ${skippedCount} تم تجاهله
        ${skippedCount > 0 ? '(تم تجاهل الصفوف ذات الباركود الفارغ)' : ''}
        ${errorCount > 0 ? '(فشل استيراد بعض المنتجات لأنها غير موجودة في قاعدة البيانات)' : ''}`
      });
      
    } catch (error: any) {
      console.error('Error processing Excel file:', error);
      setAlert({
        type: 'error',
        message: error?.response?.data?.message || error?.message || 'حدث خطأ أثناء معالجة ملف الإكسل'
      });
    } finally {
      setIsExcelImportLoading(false);
      // إعادة تعيين حقل الملف
      fileInput.value = '';
    }
  };

  // وظيفة للبحث عن وحدة القياس بالاسم
  const findUnitByName = (unitName: string): { unitId: string, unitName: string } => {
    if (!unitName) {
      return getValidUnitId(defaultUnitId);
    }
    
    // البحث عن وحدة القياس بالاسم
    const matchedUnit = units.find(unit =>
      unit.name.toLowerCase() === unitName.toLowerCase()
    );
    
    if (matchedUnit) {
      return {
        unitId: matchedUnit._id,
        unitName: matchedUnit.name
      };
    }
    
    // إذا لم يتم العثور على وحدة القياس، استخدام الافتراضية
    return getValidUnitId(defaultUnitId);
  };

  // تم إزالة تعريف handleBarcodeScan المكرر هنا لأنه معرف بالفعل سابقًا في السطر 320

  // Utility function to validate and ensure a valid unit ID
  const getValidUnitId = (unitId: string, unitName: string = 'قطعة'): { unitId: string, unitName: string } => {
    // Validate MongoDB ObjectId pattern
    const isValidObjectId = (id: string) => /^[0-9a-fA-F]{24}$/.test(id);
    
    // If the provided ID is valid and exists in our units array, use it
    if (unitId && isValidObjectId(unitId)) {
      const unit = units.find(u => u._id === unitId);
      if (unit) {
        return { unitId, unitName: unit.name };
      }
    }
    
    // Try to find a unit by name
    if (unitName) {
      const unitByName = units.find(u => u.name === unitName);
      if (unitByName && isValidObjectId(unitByName._id)) {
        return { unitId: unitByName._id, unitName: unitByName.name };
      }
    }
    
    // Try to use default unit
    if (defaultUnitId && isValidObjectId(defaultUnitId)) {
      const defaultUnit = units.find(u => u._id === defaultUnitId);
      if (defaultUnit) {
        return { unitId: defaultUnitId, unitName: defaultUnit.name };
      }
    }
    
    // Last resort: get the first valid unit from the units array
    if (units.length > 0) {
      const validUnit = units.find(u => isValidObjectId(u._id));
      if (validUnit) {
        return { unitId: validUnit._id, unitName: validUnit.name };
      }
    }
    
    // If we reach here, we couldn't find a valid unit
    return { unitId: '', unitName: 'قطعة' };
  };

  // Close alert
  const handleCloseAlert = () => {
    setAlert(null);
  };

  // Format variant attributes for display
  const formatVariantAttributes = (attributes: Record<string, string>) => {
    return Object.entries(attributes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  // التحقق من الصلاحيات للوصول إلى صفحة إنشاء طلب جديد أو تعديل طلب
  const hasAccessPermission = isEditMode ?
    hasPermission('warehouse-transfer-requests', 'list', 'edit') :
    hasPermission('warehouse-transfer-requests', 'new-request', 'view_page');

  // في حالة عدم وجود صلاحية، عرض رسالة وزر للعودة
  if (!hasAccessPermission) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" color="error" gutterBottom>
          ليس لديك صلاحية للوصول إلى هذه الصفحة
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/warehouse-transfers/requests')}
          sx={{ mt: 2 }}
        >
          العودة إلى قائمة الطلبات
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Box sx={{ ml: '-12.5%', width: '125%', pr: '12.5%', mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ ml: '1%' }}>
          {isEditMode ? 'تعديل طلب تحويل بين المستودعات' : 'طلب تحويل جديد بين المستودعات'}
        </Typography>
      </Box>
      
      <Paper sx={{ p: 3, mb: 3, ml: '-12.5%', width: '125%' }}>
        <Grid container spacing={3}>
          {/* Source Warehouse */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>من المستودع</Typography>
            <Autocomplete
              options={warehouses.fromWarehouses}
              getOptionLabel={(option) => option.name}
              value={fromWarehouse}
              onChange={(event, newValue) => {
                setFromWarehouse(newValue);
                // Clear destination warehouse if same as source
                if (toWarehouse && newValue && toWarehouse._id === newValue._id) {
                  setToWarehouse(null);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="اختر المستودع المصدر"
                  variant="outlined"
                  fullWidth
                  required
                />
              )}
              disabled={isSaving}
            />
          </Grid>
          
          {/* Destination Warehouse */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>إلى المستودع</Typography>
            <Autocomplete
              options={warehouses.toWarehouses.filter(w => !fromWarehouse || w._id !== fromWarehouse._id)}
              getOptionLabel={(option) => option.name}
              value={toWarehouse}
              onChange={(event, newValue) => {
                setToWarehouse(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="اختر المستودع الوجهة"
                  variant="outlined"
                  fullWidth
                  required
                />
              )}
              disabled={isSaving}
            />
          </Grid>
          
          {/* Transfer Date */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>تاريخ التحويل</Typography>
            <TextField
              fullWidth
              type="date"
              value={transferDate}
              onChange={(e) => setTransferDate(e.target.value)}
              disabled={isSaving}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          
          {/* Notes */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="ملاحظات"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={1}
              disabled={isSaving}
            />
          </Grid>
        </Grid>
      </Paper>
      
      {/* Search, Scan, and Excel Import/Export */}
      <Paper sx={{ p: 3, mb: 3, ml: '-12.5%', width: '125%' }}>
        <Grid container spacing={2} alignItems="center">
          {/* حقل البحث */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="بحث عن منتج"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={!fromWarehouse || isSaving}
            />
          </Grid>
          
          {/* زر البحث */}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={!fromWarehouse || !searchTerm || isSaving}
              fullWidth
            >
              بحث في المخزون
            </Button>
          </Grid>
          
          {/* زر عرض كل المنتجات */}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<SearchIcon />}
              onClick={() => {
                setIsProductAttributeSearchOpen(true);
              }}
              disabled={isSaving}
              fullWidth
            >
              عرض كل المنتجات
            </Button>
          </Grid>
          
          {/* صف جديد للباركود وملفات الإكسل */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {/* حقل إدخال الباركود مباشرة */}
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: '250px' }}>
                <TextField
                  fullWidth
                  label="إدخال الباركود"
                  placeholder="أدخل الباركود هنا"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && barcode.trim() !== '') {
                      e.preventDefault();
                      handleBarcodeScan(barcode.trim());
                      setBarcode('');
                    }
                  }}
                  disabled={!fromWarehouse || isSaving}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => {
                          if (barcode.trim() !== '') {
                            handleBarcodeScan(barcode.trim());
                            setBarcode('');
                          }
                        }}
                        disabled={!barcode.trim() || !fromWarehouse || isSaving}
                      >
                        <BarcodeIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Box>
              
              {/* أزرار تنزيل ورفع ملف الإكسل */}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={handleDownloadExcelTemplate}
                sx={{ minWidth: '180px' }}
              >
                تنزيل قالب الإكسل
              </Button>
              
              <Box sx={{ position: 'relative', display: 'inline-block', minWidth: '180px' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FileUploadIcon />}
                  component="label"
                  fullWidth
                  disabled={!fromWarehouse || isSaving}
                >
                  استيراد من إكسل
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    hidden
                    onChange={handleExcelFileUpload}
                    disabled={!fromWarehouse || isSaving}
                  />
                </Button>
              </Box>
            </Box>
          </Grid>
          
          {/* زر الحفظ */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={saveTransferRequest}
              disabled={isSaving || items.length === 0 || !fromWarehouse || !toWarehouse}
              fullWidth
            >
              {isSaving ? <CircularProgress size={24} /> : isEditMode ? 'تحديث طلب التحويل' : 'إنشاء طلب تحويل جديد'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Items Table */}
      <Paper sx={{ width: '125%', overflow: 'hidden', ml: '-12.5%' }}>
        {/* Total Quantities Summary */}
        {items.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
              إجمالي الكميات: {items.reduce((total, item) => total + (item.quantity || 0), 0)} قطعة
            </Typography>
          </Box>
        )}
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>المنتج</TableCell>
                <TableCell>الخصائص</TableCell>
                <TableCell>الكمية</TableCell>
                <TableCell>الوحدة</TableCell>
                <TableCell>SKU / الباركود</TableCell>
                <TableCell>إجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length > 0 ? (
                items.map((item, index) => (
                  <TableRow
                    key={item.tempId || index}
                    sx={{
                      bgcolor: item.hasError ? 'rgba(211, 47, 47, 0.1)' : 'inherit',
                      '&:hover': {
                        bgcolor: item.hasError ? 'rgba(211, 47, 47, 0.15)' : 'action.hover',
                      },
                      border: item.hasError ? '1px solid' : 'inherit',
                      borderColor: item.hasError ? 'error.main' : 'inherit'
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {item.hasError && (
                          <Chip
                            label="خطأ"
                            color="error"
                            size="small"
                            sx={{ mr: 1, mb: 1 }}
                          />
                        )}
                        {item.productName}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {item.variantId ? (
                        <Box sx={{ maxWidth: 220 }}>
                          {Object.entries(item.variantAttributes).map(([key, value]) => (
                            <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 1 }}>
                                {key}:
                              </Typography>
                              <Typography variant="caption">
                                {String(value)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">المنتج الأساسي</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItemQuantity(index, parseInt(e.target.value))}
                        InputProps={{ inputProps: { min: 1 } }}
                        size="small"
                        disabled={isSaving}
                        error={item.hasError}
                      />
                    </TableCell>
                    <TableCell>{item.unitName}</TableCell>
                    <TableCell>
                      {item.sku && (
                        <Typography variant="caption" display="block">
                          <strong>SKU:</strong> {item.sku}
                        </Typography>
                      )}
                      {item.barcode && (
                        <Typography variant="caption" display="block">
                          <strong>باركود:</strong> {item.barcode}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => removeItem(index)}
                        disabled={isSaving}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    لا توجد منتجات مضافة بعد
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Barcode Scanner Dialog */}
      <Dialog open={isScannerOpen} onClose={() => setIsScannerOpen(false)}>
        <DialogTitle>مسح الباركود</DialogTitle>
        <DialogContent>
          <BarcodeScanner onScan={handleBarcodeScan} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsScannerOpen(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>
      
      {/* Product Search Results Dialog */}
      <Dialog open={isSearchDialogOpen} onClose={() => setIsSearchDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">نتائج البحث عن المنتجات</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                placeholder="تصفية النتائج"
                size="small"
                sx={{ width: '220px' }}
                value={filterTerm}
                onChange={(e) => setFilterTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setFilterTerm('');
                  setExpandedProducts(new Set(groupedResults.map(g => g.productId)));
                }}
              >
                عرض الكل
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {isSearching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : filteredGroupedResults.length > 0 ? (
            <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              {filteredGroupedResults.map((group) => (
                <Paper
                  key={group.productId}
                  elevation={1}
                  sx={{
                    mb: 2,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  {/* Product Header - Clickable to expand/collapse */}
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'action.hover',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.selected' }
                    }}
                    onClick={() => toggleProductExpansion(group.productId)}
                  >
                    <Box>
                      <Typography variant="h6">{group.productName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {group.variants.length} {group.variants.length === 1 ? 'منتج' : 'منتجات'} |
                        {group.variants.some(v => v.quantity > 0) ?
                          ` متوفر: ${group.variants.reduce((sum, v) => sum + v.quantity, 0)} وحدة` :
                          ' غير متوفر في المخزون'}
                      </Typography>
                    </Box>
                    <Box>
                      {expandedProducts.has(group.productId) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="primary" sx={{ mr: 1 }}>إخفاء التفاصيل</Typography>
                          <ExpandLessIcon color="primary" />
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="primary" sx={{ mr: 1 }}>عرض التفاصيل</Typography>
                          <ExpandMoreIcon color="primary" />
                        </Box>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Expanded Variants Table */}
                  {expandedProducts.has(group.productId) && (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>الخصائص</TableCell>
                            <TableCell>SKU</TableCell>
                            <TableCell>الباركود</TableCell>
                            <TableCell>الكمية المتاحة</TableCell>
                            <TableCell>الوحدة</TableCell>
                            <TableCell>إجراءات</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.variants.map((variant) => (
                            <TableRow
                              key={`${variant.product}-${variant.variantId || 'base'}`}
                              hover
                              sx={{
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                }
                              }}
                            >
                              <TableCell>
                                {variant.variantId ?
                                  formatVariantAttributes(variant.variantAttributes) :
                                  'المنتج الأساسي'}
                              </TableCell>
                              <TableCell>{variant.sku || '-'}</TableCell>
                              <TableCell>{variant.barcode || '-'}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography
                                    sx={{
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    {variant.quantity}
                                  </Typography>
                                  <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>
                                    (يمكن طلب أي كمية)
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{variant.unitName}</TableCell>
                              <TableCell>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => showQuantityDialog(variant)}
                                  disabled={false}
                                  color="primary"
                                >
                                  إضافة
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              ))}
            </Box>
          ) : (
            <Typography variant="body1" sx={{ p: 2 }}>
              لم يتم العثور على نتائج
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSearchDialogOpen(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>
      
      {/* Quantity Selection Dialog */}
      <Dialog open={!!selectedItem} onClose={cancelProductSelection}>
        <DialogTitle>تحديد الكمية</DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedItem.productName}
              </Typography>
              
              {selectedItem.variantId && (
                <Box sx={{ my: 2, p: 2, bgcolor: 'rgba(0, 0, 0, 0.03)', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    متغيرات المنتج:
                  </Typography>
                  {Object.entries(selectedItem.variantAttributes || {}).map(([key, value]) => (
                    <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                        {key}:
                      </Typography>
                      <Typography variant="body2">
                        {String(value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                <Typography variant="body1" sx={{ mr: 2 }}>
                  الكمية المتاحة:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 'bold'
                  }}
                >
                  {selectedItem.quantity} {selectedItem.unitName}
                  <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary', display: 'block' }}>
                    (يمكن طلب كميات أكبر من المتاحة، وسيسمح النظام بذلك)
                  </Typography>
                </Typography>
              </Box>
              
              <TextField
                fullWidth
                label="الكمية المطلوبة"
                variant="outlined"
                type="number"
                value={selectedQuantity}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value > 0) {
                    setSelectedQuantity(value);
                  }
                }}
                InputProps={{
                  inputProps: {
                    min: 1,
                    // تم إزالة الحد الأقصى للكمية للسماح بالكميات السالبة
                  },
                  endAdornment: <Typography variant="body2">{selectedItem.unitName}</Typography>
                }}
                error={false}
                helperText={selectedQuantity > selectedItem.quantity ?
                  `ملاحظة: الكمية المطلوبة (${selectedQuantity}) أكبر من الكمية المتاحة (${selectedItem.quantity})، سيتم قبول الطلب مع ذلك` : ''}
                sx={{ mt: 2 }}
              />
              
              {selectedItem.sku && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  SKU: {selectedItem.sku}
                </Typography>
              )}
              
              {selectedItem.barcode && (
                <Typography variant="body2" color="text.secondary">
                  الباركود: {selectedItem.barcode}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelProductSelection}>إلغاء</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (selectedItem) {
                addProductFromSearch(selectedItem, selectedQuantity);
              }
            }}
            disabled={!selectedItem || selectedQuantity <= 0}
          >
            إضافة
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Alert Snackbar */}
      <Snackbar
        open={!!alert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert?.type || 'info'} sx={{ width: '100%' }}>
          {alert?.message || ''}
        </Alert>
      </Snackbar>
      
      {/* Product Attribute Search Dialog */}
      <ProductAttributeSearch
        open={isProductAttributeSearchOpen}
        onClose={() => setIsProductAttributeSearchOpen(false)}
        onSelectVariant={handleProductAttributeSelect}
        onSelectVariants={handleMultiProductAttributeSelect}
        multiSelect
      />
    </Box>
  );
}

export default WarehouseTransferRequestForm;
